import { ErrorTypes } from '@fabric-ui/components';
import { envVariables } from '@fabric-ui/utils';
import * as Auth from 'aws-amplify/auth';
import { AuthenticationResponse, AuthSession, convertSessionToUserInfo, normalizeAuthenticationResponse } from '../auth.types';
import { AuthEventType, AuthHub, AuthHubError } from '../AuthHub';
import { retrieveConnectionDetailsFromAuthConfig, ZidAuthLocalStorageKeys } from './tokenUtils';

async function refreshTokens(refreshToken: string): Promise<AuthenticationResponse> {
  const { zidTenantServiceDomain } = retrieveConnectionDetailsFromAuthConfig();
  const tokenEndpoint = `${envVariables.VITE_WEBSERVER_API_URL}/auth-config/refresh-token`;

  const refreshRequestBody = {
    zidDomain: `https://${zidTenantServiceDomain}`,
    refreshToken,
  };

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(refreshRequestBody).toString(),
  });

  const newTokens = await response.json();

  if (!response.ok) {
    throw new Error(`Failed to refresh token: ${newTokens.error_description || newTokens.error}`);
  }

  return newTokens;
}

export const fetchAuthSession = async (): Promise<AuthSession> => {
  const refreshToken = localStorage.getItem(ZidAuthLocalStorageKeys.refreshToken);

  if (!refreshToken) {
    throw new Error('No refresh token in local storage');
  }

  try {
    const newTokensResponse: AuthenticationResponse = await refreshTokens(refreshToken);

    // Normalize response to handle both old (zidAuthenticationResponse) and new (authenticationResponse) formats
    const { tokens, session } = normalizeAuthenticationResponse(newTokensResponse);

    const { id_token: newIdToken, access_token: newAccessToken, refresh_token: newRefreshToken } = tokens;

    localStorage.setItem(ZidAuthLocalStorageKeys.idToken, newIdToken);
    localStorage.setItem(ZidAuthLocalStorageKeys.accessToken, newAccessToken);
    localStorage.setItem(ZidAuthLocalStorageKeys.refreshToken, newRefreshToken);
    localStorage.setItem(ZidAuthLocalStorageKeys.session, JSON.stringify(session));

    return {
      tokens: {
        idToken: Auth.decodeJWT(newIdToken),
        accessToken: Auth.decodeJWT(newAccessToken),
      },
      session: convertSessionToUserInfo(session),
    };
  } catch (error) {
    console.error(`${ErrorTypes.Authentication}: Forced token refresh failed.`, error);
    const authHubError: AuthHubError = {
      message: error?.toString() || 'No Error message',
      underlyingError: error?.toString() || 'No Error message',
    };
    AuthHub.dispatch({ event: AuthEventType.TokenRefreshFailure, data: { error: authHubError } });
    return { error };
  }
};
